import { useCallback, useEffect, useState } from "react";
import { Newspaper, Sparkles, Trash2, Eye, Loader2, Lightbulb, Share2 } from "lucide-react";
import { api, formatApiError, assetUrl } from "../../lib/api";

const SHARE_PLATFORMS = [
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "x", label: "X" },
];

const inputCls = "w-full bg-white border-2 border-[#23282B]/15 focus:border-[#2F5F80] focus:outline-none px-3 py-2.5 text-sm text-[#23282B]";

export function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [topic, setTopic] = useState("");
  const [publish, setPublish] = useState(true);
  const [busy, setBusy] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [msg, setMsg] = useState(null);

  const refresh = useCallback(() => {
    api.get("/blog/admin").then((r) => setPosts(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.get("/blog/suggest-topic");
      setTopic(data.topic || "");
    } catch (e) { setMsg({ kind: "error", text: formatApiError(e) }); }
    finally { setSuggesting(false); }
  };

  const generate = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const { data } = await api.post("/blog/generate", { topic: topic.trim() || null, publish });
      setMsg({ kind: "ok", text: `"${data.title}" ${publish ? "published" : "saved as draft"}.` });
      setTopic("");
      refresh();
    } catch (e) { setMsg({ kind: "error", text: formatApiError(e) }); }
    finally { setBusy(false); }
  };

  const toggleStatus = async (p) => {
    try {
      await api.patch(`/blog/${p.id}`, { status: p.status === "published" ? "draft" : "published" });
      refresh();
    } catch (e) { setMsg({ kind: "error", text: formatApiError(e) }); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This can't be undone.`)) return;
    try { await api.delete(`/blog/${p.id}`); refresh(); }
    catch (e) { setMsg({ kind: "error", text: formatApiError(e) }); }
  };

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-5 md:p-8" data-testid="blog-manager">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-2xl md:text-3xl uppercase text-[#23282B]">AI Blog</div>
          <p className="mt-1 text-sm text-[#23282B]/60">
            Gemini writes homeowner-friendly posts for <a href="/blog" target="_blank" rel="noreferrer" className="text-[#2F5F80] underline">/blog</a>. A new post also auto-publishes every Monday at 8am.
          </p>
        </div>
        <span className="font-mono-av text-xs text-[#23282B]/60">{posts.length} POST{posts.length === 1 ? "" : "S"}</span>
      </div>

      {msg && (
        <div data-testid={`blog-msg-${msg.kind}`} className={`mt-4 px-4 py-3 text-sm border-l-4 ${msg.kind === "ok" ? "bg-green-50 border-green-600 text-green-800" : "bg-red-50 border-red-500 text-red-700"}`}>
          {msg.text}
        </div>
      )}

      <div className="mt-6 border-2 border-[#2F5F80]/25 bg-[#F4F8FB] p-5">
        <div className="font-display text-lg uppercase text-[#23282B] inline-flex items-center gap-2"><Sparkles size={16} className="text-[#2F5F80]" /> Generate a Post</div>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input
            data-testid="blog-topic-input"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Topic (leave blank for an auto seasonal pick)"
            className={inputCls}
          />
          <button onClick={suggest} disabled={suggesting || busy} data-testid="blog-suggest-topic" className="shrink-0 inline-flex items-center justify-center gap-1.5 border-2 border-[#2F5F80] text-[#2F5F80] hover:bg-[#2F5F80] hover:text-white text-xs font-bold tracking-[0.1em] px-4 py-2.5 uppercase transition-colors disabled:opacity-50">
            <Lightbulb size={14} /> {suggesting ? "Thinking…" : "Suggest"}
          </button>
        </div>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-[#23282B]/80 cursor-pointer">
            <input type="checkbox" data-testid="blog-publish-toggle" checked={publish} onChange={(e) => setPublish(e.target.checked)} className="accent-[#2F5F80] w-4 h-4" />
            Publish immediately (uncheck to save as draft)
          </label>
          <button onClick={generate} disabled={busy} data-testid="blog-generate-btn" className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-xs font-bold tracking-[0.15em] px-6 py-3 uppercase transition-colors disabled:opacity-60">
            {busy ? <><Loader2 size={14} className="animate-spin" /> Writing (~30s)…</> : <><Newspaper size={14} /> Generate Post</>}
          </button>
        </div>
      </div>

      <div className="mt-6 space-y-3" data-testid="blog-post-list">
        {posts.length === 0 && (
          <div className="border-2 border-dashed border-[#23282B]/20 p-10 text-center">
            <Newspaper size={30} className="mx-auto text-[#2F5F80]" />
            <div className="mt-2 font-display text-xl uppercase text-[#23282B]">No posts yet</div>
            <p className="text-sm text-[#23282B]/60">Generate your first post above — it takes about 30 seconds.</p>
          </div>
        )}
        {posts.map((p) => (
          <PostRow key={p.id} p={p} onToggle={toggleStatus} onDelete={remove} />
        ))}
      </div>
    </section>
  );
}

function PostRow({ p, onToggle, onDelete }) {
  const [shareOpen, setShareOpen] = useState(false);
  const [platforms, setPlatforms] = useState({ facebook: true, instagram: true, linkedin: false, x: false });
  const [shareState, setShareState] = useState({ busy: false, msg: null });

  const share = async () => {
    const picked = SHARE_PLATFORMS.map((pl) => pl.key).filter((k) => platforms[k]);
    if (!picked.length) return setShareState({ busy: false, msg: { kind: "error", text: "Pick at least one platform." } });
    setShareState({ busy: true, msg: null });
    const content = `${p.title}\n\n${p.excerpt}\n\nRead the full post: ${window.location.origin}/blog/${p.slug}`;
    try {
      await api.post("/social/posts", { content, platforms: picked, photo_ids: p.header_job_id ? [p.header_job_id] : [] });
      setShareState({ busy: false, msg: { kind: "ok", text: "Shared! Check the Social tab for the post." } });
    } catch (e) {
      setShareState({ busy: false, msg: { kind: "error", text: formatApiError(e) } });
    }
  };

  return (
    <article className="border-2 border-[#23282B]/10 hover:border-[#2F5F80] transition-colors p-4 md:p-5" data-testid={`blog-row-${p.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        {p.header_image && (
          <img src={assetUrl(p.header_image)} alt="" className="w-20 h-14 object-cover border border-[#23282B]/15 shrink-0" data-testid={`blog-thumb-${p.id}`} />
        )}
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${p.status === "published" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
              {p.status}
            </span>
            {p.generated_by === "cron" && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#9CC4DB]/30 text-[#2F5F80] px-2 py-0.5">Auto-Weekly</span>}
            <span className="text-xs text-[#23282B]/50">{p.created_at?.slice(0, 10)} · {p.reading_time} min</span>
          </div>
          <div className="mt-1.5 font-display text-lg uppercase text-[#23282B] leading-tight">{p.title}</div>
          <p className="mt-1 text-xs text-[#23282B]/60 line-clamp-2">{p.excerpt}</p>
        </div>
        <div className="flex items-center gap-2">
          {p.status === "published" && (
            <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`blog-view-${p.id}`} title="View on site" className="p-2 text-[#23282B]/50 hover:text-[#2F5F80]">
              <Eye size={16} />
            </a>
          )}
          {p.status === "published" && (
            <button onClick={() => setShareOpen((o) => !o)} data-testid={`blog-share-${p.id}`} title="Share to social media" className="inline-flex items-center gap-1.5 border-2 border-[#2F5F80] text-[#2F5F80] hover:bg-[#2F5F80] hover:text-white text-[10px] font-bold uppercase tracking-[0.1em] px-3 py-2 transition-colors">
              <Share2 size={12} /> Share
            </button>
          )}
          <button onClick={() => onToggle(p)} data-testid={`blog-toggle-${p.id}`} className={`text-[10px] font-bold uppercase tracking-[0.1em] px-3 py-2 transition-colors ${p.status === "published" ? "bg-[#23282B]/70 hover:bg-[#23282B] text-white" : "bg-green-600 hover:bg-green-700 text-white"}`}>
            {p.status === "published" ? "Unpublish" : "Publish"}
          </button>
          <button onClick={() => onDelete(p)} data-testid={`blog-delete-${p.id}`} className="p-2 text-red-600 hover:text-red-800" title="Delete post">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
      {shareOpen && (
        <div className="mt-4 border-t-2 border-[#2F5F80]/20 pt-4" data-testid={`blog-share-panel-${p.id}`}>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#2F5F80]">Share to social media {p.header_job_id ? "(includes header photo)" : ""}</div>
          <div className="mt-2 flex flex-wrap items-center gap-4">
            {SHARE_PLATFORMS.map((pl) => (
              <label key={pl.key} className="inline-flex items-center gap-1.5 text-sm text-[#23282B]/80 cursor-pointer">
                <input type="checkbox" data-testid={`blog-share-${pl.key}-${p.id}`} checked={platforms[pl.key]} onChange={(e) => setPlatforms({ ...platforms, [pl.key]: e.target.checked })} className="accent-[#2F5F80] w-4 h-4" />
                {pl.label}
              </label>
            ))}
            <button onClick={share} disabled={shareState.busy} data-testid={`blog-share-submit-${p.id}`} className="ml-auto inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-[10px] font-bold uppercase tracking-[0.12em] px-4 py-2.5 transition-colors disabled:opacity-50">
              {shareState.busy ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />} Post Now
            </button>
          </div>
          {shareState.msg && (
            <div data-testid={`blog-share-msg-${p.id}`} className={`mt-3 px-3 py-2 text-xs border-l-4 ${shareState.msg.kind === "ok" ? "bg-green-50 border-green-600 text-green-800" : "bg-red-50 border-red-500 text-red-700"}`}>
              {shareState.msg.text}
            </div>
          )}
        </div>
      )}
    </article>
  );
}
